<script setup>
import { ref, computed } from 'vue';
import { submitConsultation } from '@/utils/api.js';
import { phoneCode } from '@/assets/js/areaCode.js';
import { logEvent } from '@/utils/firebase.js';

const formData = ref({
  name: '',
  contact: '', // 合并电邮和电话
  countryCode: '00852', // WhatsApp 区号，默认香港
  whatsapp: '',
  question: '',
});

const isSubmitting = ref(false); // 提交状态，防止重复点击
const showSuccessToast = ref(false); // 成功提示弹窗显示状态

// 根据当前语言获取区号数据
const phoneCodeList = computed(() => {
  return phoneCode.map((item) => ({
    code: item.code,
    value: item.value,
  }));
});

const handleSubmit = async () => {
  // 防止重复点击
  if (isSubmitting.value) {
    return;
  }

  // 表单验证
  if (!formData.value.name || !formData.value.whatsapp) {
    alert('請填寫必填項');
    return;
  }

  // 提交按钮点击埋点
  logEvent('pc_fengsui_submit_click');

  try {
    // 设置提交状态
    isSubmitting.value = true;

    // 调用 API 提交表单
    const { data, error } = await submitConsultation(formData.value);

    if (error.value) {
      console.error('Submit error:', error.value);
      alert('提交失败，请稍后重试');
      return;
    }

    // 提交成功
    showSuccessToast.value = true;

    // 重置表单
    formData.value = {
      name: '',
      contact: '',
      countryCode: '00852',
      whatsapp: '',
      question: '',
    };

    // 1秒后自动关闭弹窗
    setTimeout(() => {
      showSuccessToast.value = false;
    }, 1000);
  } catch (error) {
    console.error('Form submit error:', error);
    alert('提交失败，请稍后重试');
  } finally {
    // 无论成功或失败，都重置提交状态
    isSubmitting.value = false;
  }
};
</script>

<template>
  <section class="contact-section">
    <h2 class="section-title">聯絡我們</h2>

    <div class="contact-content">
      <!-- 联系表单 -->
      <div class="form-container">
        <form
          @submit.prevent="handleSubmit"
          class="contact-form"
        >
          <div class="form-group">
            <label
              for="name"
              class="form-label"
            >
              您的姓名
              <span class="required">*</span>
            </label>
            <input
              id="name"
              v-model="formData.name"
              type="text"
              class="form-input"
              required
            />
          </div>

          <div class="form-group">
            <label
              for="contact"
              class="form-label"
            >
              您的電話電郵
            </label>
            <input
              id="contact"
              v-model="formData.contact"
              type="text"
              class="form-input"
              placeholder="請填寫您的電話或電郵"
            />
          </div>

          <div class="form-group">
            <label
              for="whatsapp"
              class="form-label"
            >
              您的WhatsApp
              <span class="required">*</span>
            </label>
            <div class="whatsapp-input-group">
              <select
                v-model="formData.countryCode"
                class="form-select"
              >
                <option
                  v-for="(item, index) in phoneCodeList"
                  :key="item.value + index"
                  :value="item.value"
                >
                  {{ item.code }}
                </option>
              </select>
              <input
                id="whatsapp"
                v-model="formData.whatsapp"
                type="tel"
                class="form-input whatsapp-input"
                placeholder="請填寫您的WhatsApp號碼"
                required
              />
            </div>
          </div>

          <div class="form-group">
            <label
              for="question"
              class="form-label"
            >
              您想咨詢的問題?
            </label>
            <textarea
              id="question"
              v-model="formData.question"
              class="form-textarea"
              rows="5"
            ></textarea>
          </div>

          <button
            type="submit"
            class="submit-button"
            :disabled="isSubmitting"
          >
            {{ isSubmitting ? '提交中...' : '立即提交' }}
          </button>
        </form>
      </div>

      <!-- 联系信息和地图 -->
      <div class="info-container">
        <div class="contact-info">
          <p class="info-item">
            <strong>Whatsapp：</strong>
            85246407571
            <a
              style="color: #000; text-decoration: underline"
              href="https://api.whatsapp.com/send/?phone=85246665046&amp;text&amp;type=phone_number&amp;app_absent=0"
              target="_blank"
            >
              （楓燧堂-山北师兄）
            </a>
          </p>
          <p class="info-item">
            <strong>工作時間:</strong>
            戊日不開放，其他日期早10點-晚7點開放
          </p>
          <p class="info-item">
            <strong>堂口地址:</strong>
            香港荔枝角長顺街時豐中心1305室正一天師道易烊楓燧堂
          </p>
        </div>

        <div class="map-container">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3690.4551639500173!2d114.1439750760787!3d22.336436141583167!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x340407551f504d9d%3A0x133a26f415645cff!2z5pmC6LGQ5Lit5b-DIFByZW1pZXIgQ2VudHJl!5e0!3m2!1szh-CN!2sjp!4v1768273049009!5m2!1szh-CN!2sjp"
            width="100%"
            height="450"
            style="border: 0"
            allowfullscreen=""
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
            class="map-iframe"
          ></iframe>
        </div>
      </div>
    </div>

    <!-- 成功提示弹窗 -->
    <Transition name="toast">
      <div
        v-if="showSuccessToast"
        class="success-toast"
      >
        提交成功！
      </div>
    </Transition>
  </section>
</template>

<style scoped lang="scss">
.contact-section {
  width: 100%;
  padding: 20px 40px;
  background: #ffffff;
}

.section-title {
  font-size: 15px;
  color: rgb(29, 32, 35);
  text-align: center;
  margin-bottom: 20px;
}

.contact-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.form-container {
  flex: 1;
  min-width: 0;
}

.contact-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.form-label {
  font-size: 9px;
  color: #333333;
  font-weight: 500;

  .required {
    color: #e74c3c;
    margin-left: 1px;
  }
}

.form-hint {
  font-size: 8px;
  color: #999999;
  margin: 0;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 5px 5px;
  border: 1px solid #dddddd;
  border-radius: 4px;
  font-size: 8px;
  color: #333333;
  font-family: inherit;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #e5ae40;
  }
}

.whatsapp-input-group {
  display: flex;
  gap: 5px;
  align-items: center;
}

.form-select {
  flex-shrink: 0;
  width: 100px;
  padding: 5px;
  border: 1px solid #dddddd;
  border-radius: 4px;
  font-size: 8px;
  color: #333333;
  font-family: inherit;
  background: #ffffff;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #e5ae40;
  }
}

.whatsapp-input {
  flex: 1;
}

.form-textarea {
  resize: vertical;
  min-height: 50px;
}

.submit-button {
  padding: 8px 16px;
  background: #e5ae40;
  color: #000000;
  font-size: 10px;
  font-weight: 500;
  border: none;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 10px;
  align-self: center;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.info-container {
  flex: 1;
  min-width: 0;
}

.contact-info {
  margin-bottom: 30px;
}

.info-item {
  font-size: 8px;
  color: #333333;
  line-height: 1.8;
  margin-bottom: 5px;

  strong {
    color: #000000;
    font-weight: 600;
  }
}

.map-container {
  width: 100%;
  overflow: hidden;
}

.map-iframe {
  width: 100%;
  height: 260px;
  border: none;
  display: block;
}

.success-toast {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.85);
  color: #ffffff;
  padding: 15px 30px;
  border-radius: 8px;
  font-size: 16px;
  z-index: 9999;
  white-space: nowrap;
}

.toast-enter-active,
.toast-leave-active {
  transition: opacity 0.3s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
}
</style>
